import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MagnifyingGlass, ShoppingCart } from '@phosphor-icons/react';
import { getResolvedMenu, flattenProducts, defaultVariant } from '@/services/catalog';
import * as engine from '@/lib/ordering/cartEngine';
import ProductCard from '@/components/catalog/ProductCard';
import ProductCustomizer from '@/components/catalog/ProductCustomizer';
import { useCustomer, PageWrap } from './CustomerApp';
import { formatINR } from '@/lib/money';
import { toast } from 'sonner';

export default function CustomerMenu() {
  const navigate = useNavigate();
  const { storeId: paramStoreId } = useParams();
  const [searchParams] = useSearchParams();
  const { selectedStore, cart, addToCart, updateCartQty } = useCustomer();
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [customizing, setCustomizing] = useState(null);

  const storeId = paramStoreId || selectedStore?.id;

  useEffect(() => {
    const cat = searchParams.get('category');
    if (cat) setActiveCategory(cat);
  }, [searchParams]);

  useEffect(() => { if (storeId) loadMenu(); /* eslint-disable-next-line */ }, [storeId]);

  const loadMenu = async () => {
    try {
      const res = await getResolvedMenu(storeId, 'online');
      setMenu(res.data);
    } catch (err) {
      toast.error('Failed to load menu');
    } finally {
      setLoading(false);
    }
  };

  const products = useMemo(() => (menu ? flattenProducts(menu) : []), [menu]);
  const categories = useMemo(() => ['All', ...((menu?.categories || []).map((c) => c.name))], [menu]);

  const filtered = products.filter((p) => {
    const matchCat = activeCategory === 'All' || p.category_name === activeCategory;
    const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const cartCount = engine.itemCount(cart);
  const cartTotal = engine.computeTotals(cart).subtotal;

  // total qty of a product across all its cart lines
  const productQty = (product) => cart.filter((c) => c.menu_item_id === product.id).reduce((s, c) => s + c.qty, 0);

  const buildSimpleItem = (product, qty = 1) => {
    const v = defaultVariant(product);
    return engine.createCartItem(
      { id: product.id, name: product.name, price: product.effective_price ?? v?.price ?? 0, image_url: product.image_url },
      { qty, variant: v ? { id: v.id, name: v.name, price: v.price } : null, modifiers: [] }
    );
  };

  const quickAdd = (product) => {
    addToCart(buildSimpleItem(product, 1));
    toast.success(`${product.name} added`, { duration: 1200 });
  };

  const inc = (product) => {
    const item = buildSimpleItem(product, 1);
    const existing = cart.find((c) => c.signature === item.signature);
    updateCartQty(item.signature, (existing?.qty || 0) + 1);
  };

  const dec = (product) => {
    const item = buildSimpleItem(product, 1);
    const existing = cart.find((c) => c.signature === item.signature);
    if (existing) updateCartQty(item.signature, existing.qty - 1);
  };

  const confirmCustomize = (opts) => {
    const p = customizing;
    const item = engine.createCartItem(
      { id: p.id, name: p.name, price: p.effective_price ?? 0, image_url: p.image_url },
      opts
    );
    addToCart(item);
    setCustomizing(null);
    toast.success(`${p.name} added`, { duration: 1200 });
  };

  if (!storeId) { navigate('/order'); return null; }

  return (
    <PageWrap>
      <div className="min-h-screen bg-[#FAFAFA] pb-36" data-testid="menu-page">
        <div className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-gray-100">
          <div className="px-4 pt-4 pb-2 max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-lg font-bold text-gray-900">{selectedStore?.name || 'Menu'}</h2>
                <p className="text-xs text-gray-500">{products.length} items</p>
              </div>
              <button data-testid="change-store-btn" onClick={() => navigate('/order/select-store')} className="text-xs text-sspice font-semibold hover:underline">Change</button>
            </div>
            <div className="relative mb-3">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                data-testid="menu-search" type="text" placeholder="Search menu..." value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-100"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {categories.map((cat) => (
                <button
                  key={cat} data-testid={`category-${cat}`}
                  onClick={() => setActiveCategory(cat)}
                  className={`whitespace-nowrap px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${activeCategory === cat ? 'bg-sspice text-white' : 'bg-gray-50 text-gray-500 border border-gray-100'}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="px-4 pt-3 space-y-3 max-w-5xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-16"><div className="w-8 h-8 border-3 border-sspice border-t-transparent rounded-full animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-400" data-testid="menu-empty">No items found. The store may not have published its menu yet.</div>
          ) : (
            filtered.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.02, 0.3) }}>
                <ProductCard
                  product={p} qty={productQty(p)}
                  onAdd={quickAdd} onCustomize={setCustomizing} onInc={inc} onDec={dec}
                />
              </motion.div>
            ))
          )}
        </div>

        <AnimatePresence>
          {cartCount > 0 && (
            <motion.div initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 100, opacity: 0 }} className="fixed bottom-20 left-4 right-4 z-40 max-w-md mx-auto">
              <button data-testid="view-cart-btn" onClick={() => navigate('/order/cart')} className="w-full flex items-center justify-between bg-gray-900 text-white rounded-2xl px-5 py-4 shadow-lg">
                <div className="flex items-center gap-2"><ShoppingCart size={20} weight="fill" /><span className="text-sm font-semibold">{cartCount} item{cartCount > 1 ? 's' : ''}</span></div>
                <div className="flex items-center gap-2"><span className="text-sm font-bold">{formatINR(cartTotal)}</span><span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">View Cart</span></div>
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {customizing && (
          <ProductCustomizer product={customizing} onClose={() => setCustomizing(null)} onConfirm={confirmCustomize} />
        )}
      </div>
    </PageWrap>
  );
}
